import { useTranslation } from 'react-i18next';
import { Flex, Form, Input, InputNumber, Radio, Select, Space, Switch } from 'antd';
import React, { useMemo } from 'react';
import get from 'lodash/get';
import set from 'lodash/set';
import { IDENTIFIERS, ProductPackagingType } from '../../../services/exports/Constants';
import CurrencyInput from '../general/CurrencyInput';
import AllergensSelect from './AllergensSelect';
import AdditivesSelect from './AdditivesSelect';

export default function NutritionInfoFields(props) {
  const { data, update } = props;
  const { t } = useTranslation(undefined, { keyPrefix: 'Components:Form:Menu:NutritionInfoFields' });
  const onChange = (field, value) => {
    const [key, ...rest] = field.split('.');

    update(`nutrition_info_breakdown.${key}.source`, 'manual');

    if (rest.length && !data.nutrition_info_by_source.manual?.[key] && data.nutrition_info_breakdown[key]?.value) {
      const copy = structuredClone(data.nutrition_info_breakdown[key]?.value);

      set(copy, rest.join('.'), value);

      update(`nutrition_info_by_source.manual.${key}`, copy);
      update(`nutrition_info_breakdown.${key}.value`, copy);

      return;
    }

    update(`nutrition_info_by_source.manual.${field}`, value);
    update(`nutrition_info_breakdown.${key}.value${rest.length ? '.' : ''}${rest.join('.')}`, value);
  };

  return (
    <>
      <WithSourcesWrapper {...props} field="gtin">
        <Form.Item label={t('form.gtin.label')}>
          <Input value={data.nutrition_info_breakdown.gtin?.value} onChange={(e) => onChange('gtin', e.target.value)} />
        </Form.Item>
      </WithSourcesWrapper>
      <WithSourcesWrapper {...props} field="product_type">
        <Form.Item label={t('form.product_type.label')}>
          <Select
            allowClear
            showSearch={{ optionFilterProp: 'label' }}
            options={Object.values(ProductPackagingType).map((item) => ({
              label: t(`form.product_type.options.${item}`, { defaultValue: item }),
              value: item,
            }))}
            value={data.nutrition_info_breakdown.product_type?.value}
            onChange={(value) => onChange('product_type', value)}
          />
        </Form.Item>
      </WithSourcesWrapper>
      <WithSourcesWrapper {...props} field="packaging_material">
        <Form.Item label={t('form.packaging_material.label')}>
          <Input
            value={data.nutrition_info_breakdown.packaging_material?.value}
            onChange={(e) => onChange('packaging_material', e.target.value)}
          />
        </Form.Item>
      </WithSourcesWrapper>
      <WithSourcesWrapper {...props} field="pfand">
        <Form.Item label={t('form.pfand.label')}>
          <CurrencyInput
            value={data.nutrition_info_breakdown.pfand?.value}
            onChange={(value) => onChange('pfand', value)}
            placeholder={t('form.base_price.placeholder')}
          />
        </Form.Item>
      </WithSourcesWrapper>
      <WithSourcesWrapper {...props} field="brand_or_manufacturer">
        <Form.Item label={t('form.brand_or_manufacturer.label')}>
          <Input
            value={data.nutrition_info_breakdown.brand_or_manufacturer?.value}
            onChange={(e) => onChange('brand_or_manufacturer', e.target.value)}
          />
        </Form.Item>
      </WithSourcesWrapper>
      <WithSourcesWrapper {...props} field="net_quantity">
        <Form.Item label={t('form.net_quantity.label')}>
          <Space.Compact style={{ width: '100%' }}>
            <InputNumber
              value={data.nutrition_info_breakdown.net_quantity?.value?.value}
              onChange={(value) => onChange('net_quantity.value', value)}
              parser={(value) => value?.replace(',', '.')}
              min={0}
              className="w-full"
            />
            <Select
              showSearch={{ optionFilterProp: 'label' }}
              options={[
                { label: 'g', value: 'g' },
                { label: 'ml', value: 'ml' },
              ]}
              value={data.nutrition_info_breakdown.net_quantity?.value?.unit}
              onChange={(value) => onChange('net_quantity.unit', value)}
              className="!w-auto !min-w-[60px]"
            />
          </Space.Compact>
        </Form.Item>
      </WithSourcesWrapper>
      <Flex gap="8px" className="mb-md">
        <Switch
          id="base_price_required"
          checked={data.nutrition_info_breakdown.base_price?.value?.required}
          onChange={(checked) => onChange('base_price.required', checked)}
        />
        <label htmlFor="base_price_required">{t('form.base_price.label')}</label>
      </Flex>
      {data.nutrition_info_breakdown.base_price?.value?.required && (
        <Form.Item>
          <Space.Compact style={{ width: '100%' }}>
            <CurrencyInput
              value={data.nutrition_info_breakdown.base_price?.value?.value_per_unit}
              onChange={(value) => onChange('base_price.value_per_unit', value)}
              placeholder={t('form.base_price.placeholder')}
            />
            <Select
              showSearch={{ optionFilterProp: 'label' }}
              options={[
                { label: 'kg', value: 'kg' },
                { label: 'l', value: 'l' },
              ]}
              value={data.nutrition_info_breakdown.base_price?.value?.unit}
              onChange={(value) => onChange('base_price.unit', value)}
              className="!w-auto !min-w-[60px]"
            />
          </Space.Compact>
        </Form.Item>
      )}
      <WithSourcesWrapper {...props} field="alcohol_by_volume_percent">
        <Form.Item label={t('form.alcohol_by_volume_percent.label')}>
          <InputNumber
            value={data.nutrition_info_breakdown.alcohol_by_volume_percent?.value}
            onChange={(value) => onChange('alcohol_by_volume_percent', value)}
            suffix="%"
            className="w-full"
            max={100}
          />
        </Form.Item>
      </WithSourcesWrapper>
      <WithSourcesWrapper {...props} field="caffeine_mg_per_100ml">
        <Form.Item label={t('form.caffeine_mg_per_100ml.label')}>
          <InputNumber
            value={data.nutrition_info_breakdown.caffeine_mg_per_100ml?.value}
            onChange={(value) => onChange('caffeine_mg_per_100ml', value)}
            suffix="mg / 100ml"
            className="w-full"
          />
        </Form.Item>
      </WithSourcesWrapper>
      <WithSourcesWrapper {...props} field="ingredients_text">
        <Form.Item label={t('form.ingredients_text.label')}>
          <Input.TextArea
            value={data.nutrition_info_breakdown.ingredients_text?.value}
            onChange={(e) => onChange('ingredients_text', e.target.value)}
          />
        </Form.Item>
      </WithSourcesWrapper>
      <WithSourcesWrapper {...props} field="allergens_list">
        <Form.Item label={t('form.allergens_list.label')}>
          <AllergensSelect
            allowAdd
            allowClear
            mode="multiple"
            showSearch={{ optionFilterProp: 'label' }}
            extraOptions={data.nutrition_info_breakdown.allergens_list?.value}
            value={data.nutrition_info_breakdown.allergens_list?.value}
            onChange={(value) => onChange('allergens_list', value)}
          />
        </Form.Item>
      </WithSourcesWrapper>
      <WithSourcesWrapper {...props} field="additives_list">
        <Form.Item label={t('form.additives_list.label')}>
          <AdditivesSelect
            allowAdd
            allowClear
            mode="multiple"
            showSearch={{ optionFilterProp: 'label' }}
            extraOptions={data.nutrition_info_breakdown.additives_list?.value}
            value={data.nutrition_info_breakdown.additives_list?.value}
            onChange={(value) => onChange('additives_list', value)}
          />
        </Form.Item>
      </WithSourcesWrapper>
      <Form.Item label={t('form.identifiers.label')}>
        <Select
          allowClear
          mode="multiple"
          showSearch={{ optionFilterProp: 'label' }}
          options={IDENTIFIERS}
          value={data.identifiers}
          onChange={(value) => update('identifiers', value)}
        />
      </Form.Item>
      <Flex gap="8px" className="mb-md">
        <Switch
          id="has_nutrition_table"
          checked={data.has_nutrition_table}
          onChange={(checked) => update('has_nutrition_table', checked)}
        />
        <label htmlFor="has_nutrition_table">
          {t('form.has_nutrition_table.label', {
            unit: data.nutrition_info_breakdown?.net_quantity?.value?.unit ?? 'g',
          })}
        </label>
      </Flex>
      {data.has_nutrition_table && (
        <>
          <WithSourcesWrapper {...props} field="nutrition_per_100" />
          <Form.Item layout="horizontal" label={t('form.energy_kj.label')}>
            <InputNumber
              value={data.nutrition_info_breakdown.nutrition_per_100?.value?.energy_kj}
              onChange={(value) => onChange('nutrition_per_100.energy_kj', value)}
              suffix="kJ"
              className="w-full"
            />
          </Form.Item>
          <Form.Item layout="horizontal" label={t('form.energy_kcal.label')}>
            <InputNumber
              value={data.nutrition_info_breakdown.nutrition_per_100?.value?.energy_kcal}
              onChange={(value) => onChange('nutrition_per_100.energy_kcal', value)}
              suffix="kcal"
              className="w-full"
            />
          </Form.Item>
          <Form.Item layout="horizontal" label={t('form.fat_g.label')}>
            <InputNumber
              value={data.nutrition_info_breakdown.nutrition_per_100?.value?.fat_g}
              onChange={(value) => onChange('nutrition_per_100.fat_g', value)}
              suffix="g"
              className="w-full"
            />
          </Form.Item>
          <Form.Item layout="horizontal" label={t('form.saturates_g.label')}>
            <InputNumber
              value={data.nutrition_info_breakdown.nutrition_per_100?.value?.saturates_g}
              onChange={(value) => onChange('nutrition_per_100.saturates_g', value)}
              suffix="g"
              className="w-full"
            />
          </Form.Item>
          <Form.Item layout="horizontal" label={t('form.carbohydrate_g.label')}>
            <InputNumber
              value={data.nutrition_info_breakdown.nutrition_per_100?.value?.carbohydrate_g}
              onChange={(value) => onChange('nutrition_per_100.carbohydrate_g', value)}
              suffix="g"
              className="w-full"
            />
          </Form.Item>
          <Form.Item layout="horizontal" label={t('form.sugars_g.label')}>
            <InputNumber
              value={data.nutrition_info_breakdown.nutrition_per_100?.value?.sugars_g}
              onChange={(value) => onChange('nutrition_per_100.sugars_g', value)}
              suffix="g"
              className="w-full"
            />
          </Form.Item>
          <Form.Item layout="horizontal" label={t('form.protein_g.label')}>
            <InputNumber
              value={data.nutrition_info_breakdown.nutrition_per_100?.value?.protein_g}
              onChange={(value) => onChange('nutrition_per_100.protein_g', value)}
              suffix="g"
              className="w-full"
            />
          </Form.Item>
          <Form.Item layout="horizontal" label={t('form.salt_g.label')}>
            <InputNumber
              value={data.nutrition_info_breakdown.nutrition_per_100?.value?.salt_g}
              onChange={(value) => onChange('nutrition_per_100.salt_g', value)}
              suffix="g"
              className="w-full"
            />
          </Form.Item>
        </>
      )}
    </>
  );
}

function WithSourcesWrapper({ children, field, update, data }) {
  const sourcesListByField = useMemo(() => {
    if (!data?.nutrition_info_by_source) {
      return null;
    }

    return Object.entries(data.nutrition_info_by_source)
      .filter(([, value]) => !!value?.[field])
      .map(([source]) => source);
  }, [data?.nutrition_info_by_source, field]);

  const onChange = (source, field) => {
    const value = get(data.nutrition_info_by_source[source], field);
    const [key] = field.split('.');

    update(`nutrition_info_breakdown.${key}`, { source, value });
  };

  return (
    <>
      {!!sourcesListByField?.length && (
        <Form.Item label="Sources">
          <Radio.Group
            defaultValue="manual"
            value={data?.nutrition_info_breakdown?.[field]?.source}
            onChange={(e) => onChange(e.target.value, field)}
          >
            {sourcesListByField.map((source) => (
              <Radio.Button key={source} value={source} className="capitalize">
                {source}
              </Radio.Button>
            ))}
          </Radio.Group>
        </Form.Item>
      )}
      {children}
    </>
  );
}
